import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, X, SpinnerGap, Check, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { validateAddressForm } from "@/shared/checkout/addressValidation";

// Portuguese NIF checksum (paridade com NewLeadPage)
function validateNifPT(value) {
  const nif = String(value || "").trim();
  if (nif.length !== 9 || !/^[0-9]+$/.test(nif)) return false;
  const starts1 = ["1", "2", "3", "5", "6", "8"];
  const starts2 = ["45", "70", "71", "72", "74", "75", "77", "79", "90", "91", "98", "99"];
  if (!starts1.includes(nif[0]) && !starts2.includes(nif.slice(0, 2))) return false;
  let total = 0;
  for (let i = 0; i < 8; i++) total += parseInt(nif[i], 10) * (9 - i);
  const mod = total % 11;
  const check = mod < 2 ? 0 : 11 - mod;
  return check === parseInt(nif[8], 10);
}

/**
 * Reusable address create/edit modal for the CRM (paridade com MY AddressSection).
 *
 * Props:
 *  - mode: "new" | "edit"
 *  - tipoMorada: "R" (envio/residência) | "F" (faturação)
 *  - address?: existing address object when mode="edit"
 *  - onClose: fn()
 *  - onSaved: fn(savedAddress) — called after successful create/update
 */
export default function AddressEditor({ mode = "new", tipoMorada, address = null, onClose, onSaved }) {
  const [form, setForm] = useState({
    Nome: "",
    Nif: "",
    Morada: "",
    Localidade: "",
    CodigoPostal: "",
    ID_Pais: "PRT",
    Principal: "N",
  });
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // NIF lookup state — only meaningful when tipoMorada === "F" (facturação)
  const [nifLookup, setNifLookup] = useState(null);   // {found, source, ownedByCurrent, NomeConsultor, ...}
  const [nifLookupBusy, setNifLookupBusy] = useState(false);
  const [nifFormatInvalid, setNifFormatInvalid] = useState(false);

  const isInvoiceMode = tipoMorada === "F";

  // Snapshot the original NIF (in edit mode) so we don't re-check when the
  // user opens the modal without changing the field.
  const originalNif = (address && address.Nif) ? String(address.Nif).trim() : "";

  const checkNif = async (raw) => {
    const nif = (raw || "").trim();
    setNifLookup(null);
    setNifFormatInvalid(false);
    if (!isInvoiceMode || !nif || nif === originalNif) return;
    // Só valida o NIF (checksum + lookup) para Portugal. Para outros países
    // o NIF pode ter outro formato (ex.: Espanha), por isso não validamos.
    if ((form.ID_Pais || "PRT") !== "PRT") return;
    if (!validateNifPT(nif)) {
      setNifFormatInvalid(true);
      return;
    }
    setNifLookupBusy(true);
    try {
      const r = await api.post("/leads/b2bEmpresaLookup", { nif });
      setNifLookup(r.data || null);
    } catch { setNifLookup(null); }
    finally { setNifLookupBusy(false); }
  };

  useEffect(() => {
    api.get("/geral/countries").then((r) => {
      setCountries(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === "edit" && address) {
      setForm({
        Nome: address.Nome || "",
        Nif: address.Nif || "",
        Morada: address.Morada || "",
        Localidade: address.Localidade || "",
        CodigoPostal: address.CodigoPostal || "",
        ID_Pais: address.ID_Pais || "PRT",
        Principal: address.Principal || "N",
      });
    }
  }, [mode, address]);

  const label = tipoMorada === "F" ? "Morada de Faturação" : "Morada de Envio";

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const v = validateAddressForm(form);
    if (!v.ok) { setError(v.message); return; }
    setBusy(true);
    try {
      const payload = { ...form, ID_TipoMorada: tipoMorada };
      const url = mode === "edit"
        ? "/encomendas/addresses/update"
        : "/encomendas/addresses/add";
      if (mode === "edit") payload.ID_ClienteMorada = address.ID_ClienteMorada;
      const r = await api.post(url, payload);
      if (r.data?.success === false) { setError(r.data.message || "Erro ao guardar."); return; }
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Erro ao guardar.");
    } finally {
      setBusy(false);
    }
  };

  const INPUT = "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none";
  const LABEL = "text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="address-editor-modal" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-[600px] w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-black tracking-tighter text-[#0F1829] flex items-center gap-2" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            <MapPin size={20} className="text-[#002480]" />
            {mode === "new" ? `Nova ${label}` : `Editar ${label}`}
          </h2>
          <button onClick={onClose} data-testid="address-editor-close"
                  className="text-[#6B7280] hover:text-[#0F1829]"><X size={20} /></button>
        </div>

        {error && (
          <div className="mb-3 p-2.5 rounded-md bg-red-50 border border-red-200 text-[#FF2B2B] text-xs" data-testid="address-editor-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Nome *</label>
              <input className={INPUT} value={form.Nome} onChange={(e) => setForm({ ...form, Nome: e.target.value })}
                     data-testid="address-editor-nome" />
            </div>
            <div>
              <label className={LABEL}>NIF</label>
              <input className={INPUT + (
                  nifFormatInvalid
                    ? " border-red-400 bg-red-50"
                    : nifLookup?.found && nifLookup.ownedByCurrent === false
                      ? " border-red-400 bg-red-50"
                      : nifLookup?.found && nifLookup.ownedByCurrent === true
                        ? " border-emerald-400 bg-emerald-50"
                        : ""
                )}
                value={form.Nif}
                onChange={(e) => { setForm({ ...form, Nif: e.target.value }); setNifLookup(null); setNifFormatInvalid(false); }}
                onBlur={(e) => checkNif(e.target.value)}
                data-testid="address-editor-nif" />
              {isInvoiceMode && nifFormatInvalid && (
                <p className="text-[10px] text-red-700 mt-1 flex items-center gap-1" data-testid="address-editor-nif-format-error">
                  <WarningCircle size={11} /> NIF inválido (checksum não confere)
                </p>
              )}
              {isInvoiceMode && nifLookupBusy && (
                <p className="text-[10px] text-[#6B7280] mt-1 flex items-center gap-1">
                  <SpinnerGap size={11} className="animate-spin" /> A verificar...
                </p>
              )}
              {isInvoiceMode && nifLookup?.found && nifLookup.ownedByCurrent === true && (
                <p className="text-[10px] text-emerald-700 mt-1 flex items-start gap-1" data-testid="address-editor-nif-owned">
                  <CheckCircle size={11} weight="fill" className="mt-0.5 shrink-0" />
                  <span>NIF reconhecido na sua rede ({nifLookup.NomeEmpresa || nifLookup.NomeClienteFactura}). Pode prosseguir.</span>
                </p>
              )}
              {isInvoiceMode && nifLookup?.found && nifLookup.ownedByCurrent === false && (
                <div className="mt-1 px-2.5 py-2 rounded-md bg-amber-50 border border-amber-200 text-[10px] text-amber-900" data-testid="address-editor-nif-conflict">
                  <p className="flex items-start gap-1 font-medium">
                    <WarningCircle size={11} className="mt-0.5 shrink-0" />
                    <span>Atenção: NIF já consta noutra rede</span>
                  </p>
                  <p className="mt-1 ml-4">
                    Este NIF está associado a <span className="font-medium">{nifLookup.NomeEmpresa || nifLookup.NomeClienteFactura}</span>
                    {nifLookup.NomeConsultor ? <> ({nifLookup.NomeConsultor})</> : null}.
                    Confirma se é o NIF correcto para esta morada de facturação.
                  </p>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className={LABEL}>Morada *</label>
            <input className={INPUT} value={form.Morada} onChange={(e) => setForm({ ...form, Morada: e.target.value })}
                   data-testid="address-editor-morada" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={LABEL}>Localidade *</label>
              <input className={INPUT} value={form.Localidade} onChange={(e) => setForm({ ...form, Localidade: e.target.value })}
                     data-testid="address-editor-localidade" />
            </div>
            <div>
              <label className={LABEL}>Código Postal *</label>
              <input className={INPUT} value={form.CodigoPostal}
                     onChange={(e) => setForm({ ...form, CodigoPostal: e.target.value })}
                     placeholder="NNNN-NNN" data-testid="address-editor-cp" />
            </div>
            <div>
              <label className={LABEL}>País *</label>
              <select className={INPUT} value={form.ID_Pais}
                      onChange={(e) => { setForm({ ...form, ID_Pais: e.target.value }); setNifFormatInvalid(false); setNifLookup(null); }}
                      data-testid="address-editor-pais">
                {countries.length === 0 && <option value="PRT">Portugal</option>}
                {countries.map((c) => (
                  <option key={c.ID_Pais} value={c.ID_Pais}>{c.Descricao || c.Nome || c.ID_Pais}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#6B7280]">
            <input type="checkbox" checked={form.Principal === "S"}
                   onChange={(e) => setForm({ ...form, Principal: e.target.checked ? "S" : "N" })}
                   data-testid="address-editor-principal" />
            Usar como morada principal
          </label>
          <div className="pt-3 flex items-center justify-end gap-3 border-t border-[#E5E7EB]">
            <button type="button" onClick={onClose} className="text-sm text-[#6B7280] hover:text-[#0F1829]"
                    data-testid="address-editor-cancel">Cancelar</button>
            <button type="submit" disabled={busy} data-testid="address-editor-save"
                    className="px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] flex items-center gap-2 disabled:opacity-50">
              {busy ? <SpinnerGap size={14} className="animate-spin" /> : <Check size={14} />}
              {busy ? "A guardar..." : (mode === "new" ? "Criar morada" : "Guardar alterações")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
